import * as Progress from '@radix-ui/react-progress';
import cn from 'classnames';
import { FC } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';

import {
  Service,
  ServiceType,
} from '@/shared/api/protocol-ts/api/billing_new/dto_services_new_pb';
import {
  serviceGroupNamesDefaultMessage,
  serviceNamesDefaultMessage,
} from '@/shared/i18n/services';

import styles from './ServiceView.module.scss';

type ServiceViewProps = {
  className?: string;
  service: Service;
};

export const ServiceView: FC<ServiceViewProps> = (props) => {
  const { className, service } = props;

  const { formatMessage } = useIntl();

  const serviceKind = service.Kind;

  const isServiceCountable = serviceKind.case === 'Countable';
  const isStorageService = service.Types.includes(
    ServiceType.ServiceType_Expand_Storage,
  );
  const shouldShowProgress = isServiceCountable && !isStorageService;

  const serviceName =
    service.Types.length > 1
      ? formatMessage(serviceGroupNamesDefaultMessage[service.Group], {
          amount: 0,
        })
      : formatMessage(serviceNamesDefaultMessage[service.Types[0]], {
          amount: 0,
        });

  const renderProgressBar = () => {
    if (!shouldShowProgress || serviceKind.case !== 'Countable') {
      return null;
    }

    const limit = Number(serviceKind.value.Limit ?? 0);
    const leftOver = Number(serviceKind.value.Leftover ?? 0);
    const used = limit - leftOver;
    const progress = Math.round((used / limit) * 100);

    return (
      <div className={styles.progressContainer}>
        <div className={styles.progressBlock}>
          <Progress.Root className={styles.progressRoot} value={100}>
            <Progress.Indicator
              className={styles.progressIndicator}
              style={{ transform: `translateX(-${100 - progress}%)` }}
            />
          </Progress.Root>
        </div>

        <div className={cn(styles.limits, 'p3')}>
          <FormattedMessage
            id="subscriptions.billing.used_out_of_limit"
            defaultMessage="{used} out of {limit} used"
            values={{
              used: <span className={styles.used}>{used}</span>,
              limit,
            }}
          />
        </div>
      </div>
    );
  };

  return (
    <div
      className={cn(styles.container, className)}
      data-component="inventory-service-view"
    >
      <div className={cn(styles.serviceName, 'p2')}>
        {serviceName}
        {shouldShowProgress && ':'}
      </div>

      {renderProgressBar()}
    </div>
  );
};
